import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import AgenticGenieLogo from './assets/AgenticGenie.png';
import { useSystemApi } from './apis/system-apis';

const LoginForm = () => {
  const navigate = useNavigate();
  const { signInWithGoogle, isAuthenticated, isLoading, setSystemInfo } = useAuth();
  const { getSystemInfo } = useSystemApi();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [loginError, setLoginError] = React.useState('');

  // Mock user for local testing
  const mockUser = {
    email: 'test@example.com',
    name: 'Test User',
    // Add other required properties
  };

  // Use useCallback to memoize the fetchSystemInfo function
  const fetchSystemInfo = useCallback(async () => {
    try {
      console.log('Fetching system info in LoginForm');
      const systemInfo = await getSystemInfo();
      console.log('System info:', systemInfo);
      
      // Use the enhanced setSystemInfo function that persists to localStorage
      setSystemInfo(systemInfo);
      
      // As a backup, explicitly store in localStorage
      if (systemInfo) {
        localStorage.setItem('systemInfo', JSON.stringify(systemInfo));
      }
    } catch (error) {
      console.error('Error fetching system info:', error);
      
      // Try to use cached system info if available
      const cachedSystemInfo = localStorage.getItem('systemInfo');
      if (cachedSystemInfo) {
        try {
          const parsedInfo = JSON.parse(cachedSystemInfo);
          setSystemInfo(parsedInfo);
          console.log('Using cached system info:', parsedInfo);
        } catch (parseError) {
          console.error('Error parsing cached system info:', parseError);
        }
      }
    }
  }, [getSystemInfo, setSystemInfo]);

  useEffect(() => {
    // Fetch system info only once when component mounts
    fetchSystemInfo();
    
    // This part of the effect handles navigation based on auth state
    if (!isLoading && isAuthenticated) {
      navigate('/marketplace/skills', { replace: true });
    }
    
    // Skip authentication in local testing
    if (process.env.REACT_APP_SKIP_AUTH === 'true') {
      console.log('skipping auth in LoginForm');
      navigate('/marketplace/skills', { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate]); // Remove fetchSystemInfo from dependencies

  const handleGoogleSignIn = async () => {
    setLoginError('');
    setIsSubmitting(true);
    
    try {
      if (process.env.REACT_APP_SKIP_AUTH === 'true') {
        // Mock successful sign-in
        navigate('/marketplace/skills', { replace: true });
      } else {
        await signInWithGoogle();
      }
      // Navigation will be handled by the useEffect hook when isAuthenticated changes
      console.log('signInWithGoogle from handleGoogleSignIn');
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('An error occurred during sign in. Please try again.');
    } finally {
      console.log('signInWithGoogle finally from handleGoogleSignIn');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
        <div>
          <div className="flex justify-center mb-6">
            <img src={AgenticGenieLogo} alt="Agentic Genie" className="h-40 w-auto" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your Marketplace account
          </h2>
        </div>
        
        <div className="mt-8 space-y-6">
          {loginError && (
            <div className="bg-red-50 text-red-500 p-4 rounded-md text-sm">
              {loginError}
            </div>
          )}

          <button
            onClick={handleGoogleSignIn}
            disabled={isSubmitting}
            className={`group relative w-full flex items-center justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <img
              src="https://www.google.com/favicon.ico"
              alt="Google"
              className="h-5 w-5 mr-2"
            />
            {isSubmitting ? 'Signing in...' : 'Sign in with Google'}
          </button>

        </div>
      </div>
    </div>
  );
};

export default LoginForm;